import { useState, useEffect, useRef } from "react"
import Oclock from "../common/Oclock"
import LocaltionSelect from "../common/LocationSelect"
import Realtime, { Alarms } from "./Realtime"
import ProjectList from "./ProjectList"
import { getOrgReport } from "../../api/dashboard";
import { Select } from "antd";
import DeviceSummary from "./Charts/DeviceSummary";
import AlarmTypesPie from "./Charts/AlarmTypesPie";
import OnlineRatioAxis from "./Charts/OnlineRatioAxis";
import ProjectNumColumns from "./Charts/ProjectNumColumns";
import styles from './index.module.scss'
import { useLayout } from "../../hooks";
import { Title } from "./Layouts";
import { useApp } from "../../model"

export default function Dashboard() {
    const { currentUser } = useApp();
    const [orgId, setOrgId] = useState<string | number>();
    const [orgReport, setOrgReport] = useState<OrgReport>();
    const [refreshKey, setRefreshKey] = useState(0);
    const [loading, setLoading] = useState(true);
    const [fullScreen, setFullScreen] = useState(true);
    const [layoutType, setLayoutType] = useState('3x');
    const loadingRef = useRef(true);
    const loadedRef = useRef(false);
    const requestSeqRef = useRef(0);
    const { width, height, columnGap } = useLayout(1, 1, 0.01, fullScreen);
    const layoutConfig = {
        width,
        height,
        columnGap,
        left: {
            width: 0.25 * width,
            height: 1 * height - columnGap * 2,
            control: {
                height: 0.2 * (height - columnGap * 2),
            },
            table: {
                height: 0.39 * (height - columnGap * 2),
            },
        },
        right: {
            width: 0.74 * width,
            height: 1 * height - columnGap * 2,
            charts: {
                width: 1 * 0.74 * width,
                height: 0.22 * 1 * (height - columnGap * 2),
            },
            gap: 0.01 * height,
            list: {
                width: 1 * 0.74 * width,
                height: 0.77 * 1 * (height - columnGap * 2),
            }
        }
    };
    useEffect(() => {
        const org = currentUser?.organizations?.[0];
        if (!org) return;
        setOrgId(org.organizationId)
    }, [currentUser])

    useEffect(() => {
        loadedRef.current = false;
    }, [orgId])

    useEffect(() => {
        const intervalId = setInterval(() => {
            if (loadingRef.current || !loadedRef.current) return;
            setRefreshKey(prev => prev + 1)
        }, 10 * 60 * 1000);
        return () => {
            clearInterval(intervalId)
        }
    }, []);

    useEffect(() => {
        if (!orgId) return;
        const requestSeq = ++requestSeqRef.current;
        loadingRef.current = true;
        setLoading(true)

        getOrgReport(orgId)
            .then((res: OrgReport) => {
                if (requestSeq !== requestSeqRef.current) return;
                loadedRef.current = true;
                setOrgReport({
                    ...res,
                    projectReportInfoList: [
                        ...res.projectReportInfoList,
                    ]
                })
            })
            .catch(() => {
                if (requestSeq !== requestSeqRef.current) return;
                loadedRef.current = false;
            }).finally(() => {
                if (requestSeq !== requestSeqRef.current) return;
                loadingRef.current = false;
                setLoading(false)
            })
    }, [orgId, refreshKey]);

    return (
        <div style={{
            width, height, columnGap, padding: columnGap, fontSize: 0.0097 * document.documentElement.clientWidth + 'px',
        }} className={styles.container + ' ' + (fullScreen ? styles['full-screen'] : '')}>
            <section className={styles.left} style={{
                width: layoutConfig.left.width,
                height: layoutConfig.left.height,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
            }}>
                <section style={{
                    height: layoutConfig.left.control.height,
                }} className={styles['left-control']}>
                    <Title>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <LocaltionSelect size='small' className={styles.location + ' ' + styles.title} value={orgId} onChange={e => {
                                setOrgId(e)
                            }} />
                            <Select value={'布局设置'} showArrow={false} onChange={setLayoutType} size='small' className={styles.location + ' ' + styles.layoutter} options={[
                                { label: '1x1', value: '1x' },
                                { label: '2x2', value: '2x' },
                                { label: '3x3', value: '3x' },
                            ]} />
                        </div>
                    </Title>
                    <div
                        style={{ padding: 4, display: 'flex', flex: 1, flexDirection: 'column', justifyContent: 'space-around' }}>
                        <div>
                            <Oclock />
                        </div>
                        <DeviceSummary fullScreen={fullScreen} key={refreshKey} orgReport={orgReport} />
                    </div>
                </section>
                <section style={{
                    height: layoutConfig.left.table.height,
                }} className={styles['left-table']}>
                    <Realtime refreshKey={refreshKey} orgId={orgId?.toString()} fullScreen={fullScreen} />
                </section>
                <section style={{
                    height: layoutConfig.left.table.height,
                }} className={styles['left-table']}>
                    <Alarms refreshKey={refreshKey} orgId={orgId?.toString()} fullScreen={fullScreen} />
                </section>
            </section>
            <section className={styles.right}
                style={{
                    width: layoutConfig.right.width,
                    height: layoutConfig.right.height,
                }}
            >
                <section
                    key={refreshKey}
                    style={{
                        width: layoutConfig.right.charts.width,
                        height: layoutConfig.right.charts.height,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                    <AlarmTypesPie
                        style={{
                            width: layoutConfig.right.charts.width * 0.25,
                            height: layoutConfig.right.charts.height,
                        }}
                        orgId={orgId?.toString()} hideLegend round />
                    <ProjectNumColumns
                        loading={loading} orgReport={orgReport} style={{
                            width: layoutConfig.right.charts.width * 0.35,
                            height: layoutConfig.right.charts.height,
                        }} orgId={orgId?.toString()} />
                    <OnlineRatioAxis
                        style={{
                            width: layoutConfig.right.charts.width * 0.38,
                            height: layoutConfig.right.charts.height,
                        }} orgId={orgId?.toString()} />
                </section>
                <ProjectList loading={loading} layoutType={layoutType} containerLayout={{
                    width: layoutConfig.right.list.width,
                    height: layoutConfig.right.list.height,
                }} projectReportInfoList={orgReport?.projectReportInfoList} />
                <a onClick={(e) => {
                    e.preventDefault()
                    setFullScreen(!fullScreen)
                }} className={styles.toggler}>{fullScreen ? '退出全屏' : '全屏'}</a>
            </section>
        </div >
    )
}
